import React, { useState, useEffect } from "react";
import { FaSave, FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import { useOrganization } from "../../../context/OrganizationContext";
import { useAuth } from "../../../context/AuthContext";
import { usePermissions } from "../../../context/PermissionsContext";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import OrganizationDetailsSection from "./components/OrganizationDetailsSection";
import OrganizationContactsSection from "./components/OrganizationContactsSection";
import OrganizationLinksSection from "./components/OrganizationLinksSection";
import OrganizationPlanCard from "./OrganizationPlanCard";
import { SkeletonBoard } from "../../UI/DesignStandards";
import {
  stripPhoneNumber,
  formatPhoneNumberAsTyped,
  formatPhoneNumber,
} from "../../../utility/forms/formInputFormat";
import {
  sanitizeUrl,
  isValidUrl,
  sanitizeInput,
  sanitizeEmail,
  sanitizeDescription,
} from "../../../utils/sanitization";

const OrganizationProfile = () => {
  const { currentOrganization, refreshOrganizations } = useOrganization();
  const { user } = useAuth();
  const { canManageOrganization } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingOrg, setEditingOrg] = useState(false);
  const [organizationData, setOrganizationData] = useState({
    name: "",
    description: "",
    orgData: {},
  });
  const [contacts, setContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    email: "",
    phone: "",
    organizationRole: "",
  });
  const [links, setLinks] = useState([]);
  const [editingLink, setEditingLink] = useState(null);
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLink, setNewLink] = useState({
    type: "",
    url: "",
  });
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [deletingLinkId, setDeletingLinkId] = useState(null);
  const [deletingContactInProgressId, setDeletingContactInProgressId] =
    useState(null);
  const [deletingLinkInProgressId, setDeletingLinkInProgressId] =
    useState(null);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");

  // Load organization data
  useEffect(() => {
    if (currentOrganization) {
      loadOrganizationData();
    }
  }, [currentOrganization]);

  const loadOrganizationData = async () => {
    if (!currentOrganization) return;

    setLoading(true);
    try {
      // Load organization details
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select("id, name, description, orgData")
        .eq("id", currentOrganization.id)
        .single();

      if (orgError) throw orgError;

      setOrganizationData({
        name: orgData.name || "",
        description: orgData.description || "",
        orgData: orgData.orgData || {},
      });

      // Load organization contacts
      const { data: contactData, error: contactError } = await supabase
        .from("organizationContact")
        .select("*")
        .eq("organizationID", currentOrganization.id)
        .is("deletedAt", null);

      if (contactError) throw contactError;

      setContacts(contactData || []);

      // Load organization links
      const { data: linkData, error: linkError } = await supabase
        .from("organizationLinks")
        .select("*")
        .eq("organizationID", currentOrganization.id)
        .is("deletedAt", null);

      if (linkError) throw linkError;

      setLinks(linkData || []);
    } catch (error) {
      console.error("Error loading organization data:", error);
      setPageError("Failed to load organization data");
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationChange = (field, value) => {
    setOrganizationData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCustomContactChange = (key, value) => {
    setOrganizationData((prev) => ({
      ...prev,
      orgData: {
        ...prev.orgData,
        [key]: value,
      },
    }));
  };

  const handleLinkChange = (field, value) => {
    if (field === "resetNew") {
      setNewLink({ type: "", url: "" });
      return;
    }
    if (editingLink) {
      setEditingLink((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else {
      setNewLink((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const saveLink = async () => {
    const linkToSave = editingLink || newLink;

    if (!linkToSave.type || !linkToSave.url) {
      setFormError("Please fill in all required fields");
      return;
    }

    // Validate URL format
    if (!isValidUrl(linkToSave.url)) {
      setFormError(
        "Please enter a valid URL (e.g., https://example.com or www.example.com)",
      );
      return;
    }

    // Sanitize and normalize the URL
    const sanitizedUrl = sanitizeUrl(linkToSave.url);

    try {
      if (editingLink) {
        const { error } = await supabase
          .from("organizationLinks")
          .update({
            type: editingLink.type,
            url: sanitizedUrl,
            updatedAt: new Date().toISOString(),
            updatedBy: user.id,
          })
          .eq("id", editingLink.id);

        if (error) throw error;
        setEditingLink(null);
      } else {
        const { error } = await supabase.from("organizationLinks").insert({
          organizationID: currentOrganization.id,
          type: newLink.type,
          url: sanitizedUrl,
          createdBy: user.id,
          updatedBy: user.id,
        });

        if (error) throw error;
        setNewLink({ type: "", url: "" });
        setShowAddLink(false);
      }

      loadOrganizationData();
    } catch (error) {
      console.error("Error saving link:", error);
      setFormError("Failed to save link");
    }
  };

  const deleteLink = async (linkId) => {
    try {
      setDeletingLinkInProgressId(linkId);
      const { error } = await supabase
        .from("organizationLinks")
        .update({
          deletedAt: new Date().toISOString(),
          deletedBy: user.id,
        })
        .eq("id", linkId);

      if (error) throw error;
      setDeletingLinkId(null);
      loadOrganizationData();
    } catch (error) {
      console.error("Error deleting link:", error);
      setPageError("Failed to delete link");
    } finally {
      setDeletingLinkInProgressId(null);
    }
  };

  const handleContactChange = (field, value) => {
    if (field === "resetNew") {
      setNewContact({ name: "", email: "", phone: "", organizationRole: "" });
      return;
    }
    // Special handling for phone numbers
    if (field === "phone") {
      // Format the phone number as the user types
      const formattedValue = formatPhoneNumberAsTyped(value);

      if (editingContact) {
        setEditingContact((prev) => ({
          ...prev,
          [field]: formattedValue,
        }));
      } else {
        setNewContact((prev) => ({
          ...prev,
          [field]: formattedValue,
        }));
      }
    } else {
      // Handle other fields normally
      if (editingContact) {
        setEditingContact((prev) => ({
          ...prev,
          [field]: value,
        }));
      } else {
        setNewContact((prev) => ({
          ...prev,
          [field]: value,
        }));
      }
    }
  };

  const saveContact = async () => {
    const contactToSave = editingContact || newContact;
    if (!contactToSave.name) {
      setFormError("Please fill in the contact name");
      return;
    }

    // Sanitize contact data
    const sanitizedContact = {
      name: sanitizeInput(contactToSave.name),
      email: sanitizeEmail(contactToSave.email),
      phone: stripPhoneNumber(contactToSave.phone), // Store only digits
      organizationRole: sanitizeInput(contactToSave.organizationRole),
    };

    try {
      if (editingContact) {
        const { error } = await supabase
          .from("organizationContact")
          .update({
            name: sanitizedContact.name,
            email: sanitizedContact.email,
            phone: sanitizedContact.phone,
            organizationRole: sanitizedContact.organizationRole,
            updatedAt: new Date().toISOString(),
            updatedBy: user.id,
          })
          .eq("id", editingContact.id);

        if (error) throw error;
        setEditingContact(null);
      } else {
        const { error } = await supabase.from("organizationContact").insert({
          organizationID: currentOrganization.id,
          name: sanitizedContact.name,
          email: sanitizedContact.email,
          phone: sanitizedContact.phone,
          organizationRole: sanitizedContact.organizationRole,
          createdBy: user.id,
        });

        if (error) throw error;
        setNewContact({
          name: "",
          email: "",
          phone: "",
          organizationRole: "",
        });
        setShowAddContact(false);
      }

      loadOrganizationData();
    } catch (error) {
      console.error("Error saving contact:", error);
      setFormError("Failed to save contact");
    }
  };

  const deleteContact = async (contactId) => {
    try {
      setDeletingContactInProgressId(contactId);
      const { error } = await supabase
        .from("organizationContact")
        .update({
          deletedAt: new Date().toISOString(),
          deletedBy: user.id,
        })
        .eq("id", contactId);

      if (error) throw error;
      setDeletingContactId(null);
      loadOrganizationData();
    } catch (error) {
      console.error("Error deleting contact:", error);
      setPageError("Failed to delete contact");
    } finally {
      setDeletingContactInProgressId(null);
    }
  };

  const saveOrganization = async () => {
    if (!organizationData.name.trim()) {
      setFormError("Organization name is required");
      return;
    }

    // Sanitize organization data
    const sanitizedOrgData = {
      name: sanitizeInput(organizationData.name),
      description: sanitizeDescription(organizationData.description),
      orgData: organizationData.orgData,
    };

    setSaving(true);
    try {
      const { error } = await supabase
        .from("organizations")
        .update({
          name: sanitizedOrgData.name,
          description: sanitizedOrgData.description,
          orgData: sanitizedOrgData.orgData,
        })
        .eq("id", currentOrganization.id);

      if (error) throw error;
      setEditingOrg(false);
      refreshOrganizations();
    } catch (error) {
      console.error("Error saving organization:", error);
      setFormError("Failed to save organization profile");
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingOrg(false);
    // Reset to original data
    loadOrganizationData();
  };

  if (loading) {
    return (
      <div
        className="space-y-6"
        role="status"
        aria-label="Loading organization"
      >
        <SkeletonBoard rows={4} />
        <SkeletonBoard rows={3} />
        <SkeletonBoard rows={3} />
      </div>
    );
  }

  // Check permissions
  if (!canManageOrganization) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-brand-cream rounded-lg flex items-center justify-center mx-auto mb-4 border border-brand-charcoal/10">
            <svg
              className="w-8 h-8 text-brand-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="heading-display text-2xl mb-2">Access Denied</h2>
          <p className="text-brand-muted">
            You don't have permission to manage this organization's profile.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto space-y-6 px-4 sm:px-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
            Profile
          </h1>
          <p className="text-sm text-brand-mist mt-1">
            Name, contacts, links, and DPA status.
          </p>
        </div>
      </header>
      <OrganizationPlanCard />
      {pageError ? (
        <InlineAlert
          type="error"
          title="There was a problem"
          message={pageError}
        />
      ) : null}
      <OrganizationDetailsSection
        editingOrg={editingOrg}
        organizationData={organizationData}
        saving={saving}
        onEdit={() => setEditingOrg(true)}
        onCancel={cancelEdit}
        onChange={handleOrganizationChange}
        onSave={saveOrganization}
        formError={formError}
      />

      <OrganizationContactsSection
        contacts={contacts}
        formError={formError}
        showAddContact={showAddContact}
        editingContact={editingContact}
        newContact={newContact}
        onSetShowAddContact={setShowAddContact}
        onSetEditingContact={setEditingContact}
        onContactChange={handleContactChange}
        onSaveContact={saveContact}
        deletingContactId={deletingContactId}
        deletingContactInProgressId={deletingContactInProgressId}
        onStartConfirmDelete={setDeletingContactId}
        onCancelConfirmDelete={() => setDeletingContactId(null)}
        onConfirmDelete={deleteContact}
      />

      <OrganizationLinksSection
        links={links}
        showAddLink={showAddLink}
        editingLink={editingLink}
        newLink={newLink}
        formError={formError}
        onSetShowAddLink={setShowAddLink}
        onSetEditingLink={setEditingLink}
        onLinkChange={handleLinkChange}
        onSaveLink={saveLink}
        deletingLinkId={deletingLinkId}
        deletingLinkInProgressId={deletingLinkInProgressId}
        onStartConfirmDelete={setDeletingLinkId}
        onCancelConfirmDelete={() => setDeletingLinkId(null)}
        onConfirmDelete={deleteLink}
      />
    </div>
  );
};

export default OrganizationProfile;
